/* Which screen is showing, and the registry of the ones that can.
 *
 * The console's apps.js is the model for this: a page registers itself, its file
 * is imported for that side effect alone from main.js, and nothing here imports
 * a page. That is not ceremony at this size — it is what stops this module and
 * the pages from importing each other, which they otherwise would the first time
 * a page wants to link to another one. `nav.goPage('ingest')` is available to
 * every page without any of them being able to reach into another's file.
 *
 * Registration ORDER is the sidebar order, unlike the console's registry, which
 * sorts alphabetically. The sidebar is a walk through a salon's configuration —
 * what it is, what it looks like, how it behaves, who works there, what it is
 * connected to, and then the things that break it — and alphabetical would put
 * Danger zone second.
 *
 * ── THE URL ─────────────────────────────────────────────────────────────────
 *
 * Hash routing, so the built app is a single file that can be served from any
 * path with no server rewrites — the same reason vite.config.js sets `base:
 * './'`. The three shapes:
 *
 *   #/tenants                     the list
 *   #/tenants/<uuid>/<page>       one salon, one page
 *   #/admins                      the platform admins
 *   #/plans                       the licence catalogue
 *
 * A URL that names a tenant is worth having: it is what gets pasted into a
 * message that says "look at this one".
 *
 * The last two are the screens that belong to the PRODUCT rather than to a
 * customer, which is why they are top-level rather than registered pages: there
 * is no salon they could sit inside, and putting either in the sidebar would
 * mean picking a salon first to reach something that is not about one.
 */
import * as Vue from 'vue';

var registry = Vue.reactive([]);

/* `markRaw` on the component itself, and it is not cosmetic.
 *
 * The registry is reactive so the sidebar redraws as pages register. Everything
 * pushed into it is therefore deep-proxied, and a Vue component object handed to
 * `<component :is>` as a proxy is a component Vue has to unwrap on every render
 * — it warns about exactly this. The page's own entry stays reactive; the
 * component definition inside it does not need to be, because it never changes.
 */
function register(page) {
  if (registry.some(function (p) { return p.id === page.id; })) return;
  registry.push(Object.assign({ group: 'Settings' }, page, {
    page: Vue.markRaw(page.page)
  }));
}

function pages() { return registry; }

function get(id) {
  return registry.find(function (p) { return p.id === id; }) || null;
}

/* The sidebar's sections, built from what has registered rather than declared
 * up front — a group with nothing in it does not draw a heading, so removing a
 * page is deleting one import. */
var groups = Vue.computed(function () {
  var out = [];
  registry.forEach(function (p) {
    var g = out.find(function (x) { return x.name === p.group; });
    if (!g) { g = { name: p.group, items: [] }; out.push(g); }
    g.items.push(p);
  });
  return out;
});

var route = Vue.reactive({
  screen: 'tenants',   // tenants | tenant | admins | plans
  tenantId: null,
  pageId: null
});

var UUID = /^[0-9a-f-]{36}$/i;

function parse(hash) {
  var parts = String(hash || '').replace(/^#\/?/, '').split('/').filter(Boolean);

  if (parts[0] === 'admins') return { screen: 'admins', tenantId: null, pageId: null };
  if (parts[0] === 'plans')  return { screen: 'plans',  tenantId: null, pageId: null };

  if (parts[0] === 'tenants' && parts[1] && UUID.test(parts[1])) {
    return {
      screen: 'tenant',
      tenantId: parts[1],
      // An unknown page id falls back to the first registered page rather than
      // rendering nothing. A link written against a page that has since been
      // renamed should still land somewhere.
      pageId: (parts[2] && get(parts[2]) ? parts[2] : (registry[0] && registry[0].id)) || null
    };
  }

  return { screen: 'tenants', tenantId: null, pageId: null };
}

function apply() {
  Object.assign(route, parse(window.location.hash));
}

window.addEventListener('hashchange', apply);

function goList() { window.location.hash = '#/tenants'; }

function goTenant(id, pageId) {
  window.location.hash = '#/tenants/' + id + '/' + (pageId || (registry[0] && registry[0].id) || '');
}

// Same tenant, different page — what a page uses to send somebody to another
// one without knowing which tenant it is looking at.
function goPage(pageId) {
  if (!route.tenantId) return;
  goTenant(route.tenantId, pageId);
}

function goAdmins() { window.location.hash = '#/admins'; }

function goPlans() { window.location.hash = '#/plans'; }

var active = Vue.computed(function () {
  return route.screen === 'tenant' ? get(route.pageId) : null;
});

export const nav = {
  register: register,
  pages: pages,
  get: get,
  groups: groups,
  route: route,
  active: active,
  parse: parse,
  apply: apply,
  goList: goList,
  goTenant: goTenant,
  goPage: goPage,
  goAdmins: goAdmins,
  goPlans: goPlans
};
